import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Edit } from "react-feather";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    FormGroup,
    Input,
    Label,
} from "reactstrap";
import Breadcrumb from "../common/breadcrumb";
import { useSelector } from "react-redux";
import { getFeedbackSubjects, createFeedbackSubject, updateFeedbackSubject } from "../../utils/apiCalls";
import Loader from "../../shared/Loader";

const FeedbackSubjectList = () => {
    const userType = localStorage.getItem("userType");
    const merchantID = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const selectedMerchant = userType === "merchant" ? merchantID : reduxSelectedMerchant;

    const [isLoading, setIsLoading] = useState(false);
    const [subjects, setSubjects] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingSubject, setEditingSubject] = useState(null);
    const [formData, setFormData] = useState({ title: "", active: true });
    const [statusFilter, setStatusFilter] = useState("");

    const fetchSubjects = useCallback(async () => {
        setIsLoading(true);
        const merchantParam = selectedMerchant && selectedMerchant !== "all" ? selectedMerchant : undefined;
        const activeParam = statusFilter === "" ? undefined : statusFilter;
        const data = await getFeedbackSubjects(merchantParam, activeParam);
        setSubjects(data || []);
        setIsLoading(false);
    }, [selectedMerchant, statusFilter]);

    useEffect(() => {
        fetchSubjects();
    }, [fetchSubjects]);

    const openCreateModal = () => {
        setEditingSubject(null);
        setFormData({ title: "", active: true });
        setModalOpen(true);
    };

    const openEditModal = (subject) => {
        setEditingSubject(subject);
        setFormData({ title: subject.title, active: subject.active });
        setModalOpen(true);
    };

    const handleSubmit = async () => {
        if (!formData.title.trim()) return;

        if (editingSubject) {
            const result = await updateFeedbackSubject(editingSubject.id, formData);
            if (result) {
                setModalOpen(false);
                fetchSubjects();
            }
        } else {
            const merchantId = selectedMerchant && selectedMerchant !== "all" ? selectedMerchant : null;
            if (!merchantId) return;
            const result = await createFeedbackSubject({ title: formData.title, merchantId });
            if (result) {
                setModalOpen(false);
                fetchSubjects();
            }
        }
    };

    return (
        <Fragment>
            <Breadcrumb title="Feedback Subjects" parent="Orders" />
            <Container fluid={true}>
                <Row>
                    <Col className="d-flex justify-content-end gap-3" style={{ marginBottom: "30px" }}>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{
                                padding: "10px",
                                fontSize: "16px",
                                borderRadius: "5px",
                                border: "1px solid #009689",
                            }}
                        >
                            <option value="">All Status</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </select>
                        <Button
                            color="primary"
                            style={{
                                padding: "10px 20px",
                                fontSize: "16px",
                                borderRadius: "5px",
                            }}
                            onClick={openCreateModal}
                            disabled={!selectedMerchant || selectedMerchant === "all"}
                        >
                            Add Subject
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardBody>
                                {isLoading ? (
                                    <Loader />
                                ) : subjects.length === 0 ? (
                                    <div style={{ textAlign: "center", padding: "40px" }}>
                                        {!selectedMerchant || selectedMerchant === "all"
                                            ? "Please select a merchant to view feedback subjects."
                                            : "No feedback subjects found."}
                                    </div>
                                ) : (
                                    <Table responsive>
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Title</th>
                                                <th>Status</th>
                                                <th>Created At</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {subjects.map((subject) => (
                                                <tr key={subject.id} style={{ verticalAlign: "middle" }}>
                                                    <td>{subject.id}</td>
                                                    <td>{subject.title}</td>
                                                    <td>
                                                        <span
                                                            style={{
                                                                padding: "4px 10px",
                                                                borderRadius: "4px",
                                                                fontSize: "12px",
                                                                fontWeight: "bold",
                                                                color: "#fff",
                                                                background: subject.active ? "#28a745" : "#dc3545",
                                                            }}
                                                        >
                                                            {subject.active ? "Active" : "Inactive"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {new Date(subject.createdAt).toLocaleString("en-GB", {
                                                            day: "2-digit",
                                                            month: "short",
                                                            year: "numeric",
                                                        })}
                                                    </td>
                                                    <td>
                                                        <Button
                                                            color="btn"
                                                            type="button"
                                                            onClick={() => openEditModal(subject)}
                                                        >
                                                            <Edit
                                                                className="editBtn"
                                                                style={{ width: "16px", height: "16px" }}
                                                            />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
                    <ModalHeader toggle={() => setModalOpen(false)}>
                        {editingSubject ? "Edit Feedback Subject" : "Add Feedback Subject"}
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label>Title</Label>
                            <Input
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="Enter subject title"
                            />
                        </FormGroup>
                        {editingSubject && (
                            <FormGroup>
                                <Label>Status</Label>
                                <Input
                                    type="select"
                                    value={formData.active ? "true" : "false"}
                                    onChange={(e) =>
                                        setFormData({ ...formData, active: e.target.value === "true" })
                                    }
                                >
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </Input>
                            </FormGroup>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button color="primary" onClick={handleSubmit} disabled={!formData.title.trim()}>
                            {editingSubject ? "Update" : "Create"}
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </Fragment>
    );
};

export default FeedbackSubjectList;
